import {
  Check, Clock, Warning, Crown, Sword, SortAscending, MapPin
} from "@phosphor-icons/react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function WizardStepVenue({ venues, selectedVenueIds, toggleVenue, form, getVenueAvailability }) {
  return (
    <div className="space-y-5" data-testid="wizard-step-venue">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold">Select Venue / Tables (multi-select)</Label>
        <Badge variant="outline" className="text-primary border-primary/30">{selectedVenueIds.length} selected</Badge>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {venues.map(v => {
          const avail = getVenueAvailability(v);
          const isSelected = selectedVenueIds.includes(v.id);
          return (
            <button key={v.id} onClick={() => toggleVenue(v.id)}
              className={`text-left p-4 rounded-lg border-2 transition-all ${
                isSelected ? "border-primary bg-primary/10" : "border-border hover:border-primary/40 bg-card"
              }`} data-testid={`venue-card-${v.id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-foreground">{v.name}</span>
                {isSelected && <Check className="w-5 h-5 text-primary" weight="bold" />}
              </div>
              {v.description && <p className="text-sm text-muted-foreground mb-2">{v.description}</p>}
              {avail ? (
                <div className="flex items-center gap-2 text-xs text-primary"><Clock className="w-3 h-3" />Available {avail.start_time} - {avail.end_time}</div>
              ) : form.date ? (
                <div className="flex items-center gap-2 text-xs text-destructive"><Warning className="w-3 h-3" />Not available on selected date</div>
              ) : null}
              {v.availability?.length > 0 && (
                <div className="flex gap-1 mt-2">
                  {DAYS.map((d, i) => {
                    const hasSlot = v.availability?.some(a => a.day_of_week === i);
                    return <span key={d} className={`text-[10px] px-1.5 py-0.5 rounded ${hasSlot ? "bg-primary/20 text-primary" : "bg-muted/30 text-muted-foreground"}`}>{d}</span>;
                  })}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function WizardStepPlayers({
  players, externalPlayers, selectedInternal, selectedExternal,
  setSelectedInternal, setSelectedExternal, playerSearch, setPlayerSearch,
  form, selectAllInternal, deselectAllInternal, selectByRanking,
  totalSelected, filteredPlayers, hasConflict, togglePlayer
}) {
  return (
    <div className="space-y-5" data-testid="wizard-step-players">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Label className="text-base font-semibold">Select Players</Label>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className={`${totalSelected >= form.min_players ? "text-primary border-primary/30" : "text-destructive border-destructive/30"}`}>
            {totalSelected} / {form.min_players} min
          </Badge>
          <Button size="sm" variant="outline" onClick={selectAllInternal} className="h-7 text-xs border-border" data-testid="select-all-btn">Select All</Button>
          <Button size="sm" variant="outline" onClick={deselectAllInternal} className="h-7 text-xs border-border">Clear</Button>
          <Select onValueChange={v => selectByRanking(parseInt(v))}>
            <SelectTrigger className="h-7 w-32 text-xs bg-background border-border">
              <SortAscending className="w-3 h-3 mr-1" /><SelectValue placeholder="By Rank" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="8">Top 8</SelectItem>
              <SelectItem value="12">Top 12 (Premier)</SelectItem>
              <SelectItem value="16">Top 16</SelectItem>
              <SelectItem value="24">Top 24</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <Input placeholder="Search players..." value={playerSearch} onChange={e => setPlayerSearch(e.target.value)} className="bg-background border-border" data-testid="player-search-input" />
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Club Members ({filteredPlayers.length})</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto p-1">
          {filteredPlayers.map(p => {
            const conflict = hasConflict(p);
            const isSelected = selectedInternal.includes(p.id);
            return (
              <label key={p.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all border ${isSelected ? "border-primary/40 bg-primary/5" : conflict ? "border-destructive/30 bg-destructive/5" : "border-transparent hover:bg-muted/20"}`} data-testid={`player-checkbox-${p.id}`}>
                <Checkbox checked={isSelected} onCheckedChange={() => togglePlayer(p.id, selectedInternal, setSelectedInternal)} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2"><span className="text-sm font-medium truncate">{p.name}</span>{p.tier === "premier" && <Crown className="w-3.5 h-3.5 text-secondary flex-shrink-0" weight="fill" />}</div>
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground"><span>#{p.ranking}</span>{p.handicap > 0 && <span className="text-chart-3">HC:{p.handicap}</span>}{conflict && <span className="text-destructive flex items-center gap-0.5"><Warning className="w-3 h-3" /> Unavailable</span>}</div>
                </div>
              </label>
            );
          })}
        </div>
      </div>
      {!form.is_internal && externalPlayers.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">External Players ({externalPlayers.length})</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-40 overflow-y-auto p-1">
            {externalPlayers.map(p => {
              const isSelected = selectedExternal.includes(p.id);
              return (
                <label key={p.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all border ${isSelected ? "border-chart-3/40 bg-chart-3/5" : "border-transparent hover:bg-muted/20"}`} data-testid={`ext-player-checkbox-${p.id}`}>
                  <Checkbox checked={isSelected} onCheckedChange={() => togglePlayer(p.id, selectedExternal, setSelectedExternal)} />
                  <div className="flex-1 min-w-0"><span className="text-sm font-medium truncate">{p.name}</span><p className="text-[11px] text-chart-3">{p.club_name}</p></div>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function WizardStepOrganizers({ players, selectedOrganizers, togglePlayer, setSelectedOrganizers }) {
  return (
    <div className="space-y-5" data-testid="wizard-step-organizers">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold">Select Organizers (optional)</Label>
        <Badge variant="outline" className="text-secondary border-secondary/30">{selectedOrganizers.length} selected</Badge>
      </div>
      <p className="text-sm text-muted-foreground">Organizers can manage matches and update scores for this tournament.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto p-1">
        {players.map(p => {
          const isSelected = selectedOrganizers.includes(p.id);
          return (
            <label key={p.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all border ${isSelected ? "border-secondary/40 bg-secondary/5" : "border-transparent hover:bg-muted/20"}`} data-testid={`organizer-checkbox-${p.id}`}>
              <Checkbox checked={isSelected} onCheckedChange={() => togglePlayer(p.id, selectedOrganizers, setSelectedOrganizers)} />
              <div className="flex items-center gap-2"><span className="text-sm font-medium truncate">{p.name}</span>{isSelected && <Sword className="w-3.5 h-3.5 text-secondary" weight="fill" />}</div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export function WizardStepReview({ form, selectedVenueIds, venues, totalSelected, selectedOrganizers, players }) {
  return (
    <div className="space-y-5" data-testid="wizard-step-review">
      <Label className="text-base font-semibold">Review & Create</Label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 rounded-lg bg-muted/20 border border-border space-y-2">
          <p className="text-xs text-muted-foreground uppercase font-semibold">Tournament</p>
          <p className="font-bold text-lg">{form.name || "Unnamed"}</p>
          <div className="flex gap-2 flex-wrap">
            <Badge>{form.tournament_type}</Badge>
            <Badge variant="outline">{form.tournament_format?.replace('_', ' + ')}</Badge>
            <Badge variant="outline">Bo{form.best_of}</Badge>
          </div>
          {form.date && <p className="text-sm text-muted-foreground">{form.date}{form.end_date ? ` to ${form.end_date}` : ''}</p>}
          {form.year && <p className="text-sm text-muted-foreground">Year: {form.year}</p>}
        </div>
        <div className="p-4 rounded-lg bg-muted/20 border border-border space-y-2">
          <p className="text-xs text-muted-foreground uppercase font-semibold">Setup</p>
          <div className="space-y-1 text-sm">
            <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-muted-foreground" />{selectedVenueIds.length} venue(s): {venues.filter(v => selectedVenueIds.includes(v.id)).map(v => v.name).join(', ') || 'None'}</p>
            <p className="flex items-center gap-2">{totalSelected} players registered</p>
            {selectedOrganizers.length > 0 && <p className="flex items-center gap-2"><Sword className="w-4 h-4 text-secondary" />{selectedOrganizers.length} organizer(s): {players.filter(p => selectedOrganizers.includes(p.id)).map(p => p.name).join(', ')}</p>}
            {form.is_internal && <Badge variant="outline" className="text-xs">Internal Only</Badge>}
          </div>
        </div>
      </div>
    </div>
  );
}
